import './location-selector.scss'

import { faBarcode, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { debounceTime, first, mergeMap } from 'rxjs/operators';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import { LocationDataStore, locationSelectors } from '@skinsoft/entity/src/location';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { BarcodeReader, BarcodeSubscriber, getTranslatedString } from '@skinsoft/ui';
import _ from 'lodash';

@Component({
    components: {
        sBarcodeReader: BarcodeReader,
        sBarcodeSubscriber: BarcodeSubscriber
    },
    props: [''],
    template: require('./location-selector.html')
})
export class LocationSelector extends ReactiveVue {

    private $locationDataStore: LocationDataStore;

    private locations: any[] = []
    private searchText: string = null as any;

    private value$ = new Subject();
    private pending: boolean = true;
    private changePagePending: boolean = false;
    private currentPageIndex: number = 1;
    private lastPageIndex: number = 1;
    private readonly pageSize = 15;
    private isBarcodeReaderActive: boolean = false;
    private displayPaths: boolean = false;

    // By default, no item is selected
    private selectedItemIndex: number = -1;

    // Icons
    private loadingIcon = faCircleNotch;
    private barcodeIcon = faBarcode;

    public mounted() {
        // Subscribe to text search
        this.subscribeTo(
            this.value$.pipe(
                debounceTime(750),
            ),
            searchText => {
                this.subscribeToLocations(true)
            }
        )
        // used to trigger watcher the first time
        this.searchText = ""
    }

    private subscribeToLocations(resetPageIndex?: boolean) {
        this.unsubscribeFrom('locations-subscription')
        this.pending = true;
        this.displayPaths = false;
        this.subscribeTo(this.$locationDataStore.searchAll(this.searchText, null, this.pageSize, undefined, this.currentPageIndex - 1).pipe(
            first()
        ),
            searchResponse => {
                this.locations = searchResponse.allData;
                Promise.all(this.locations.map(
                    async location => {
                        location.indexTitle = locationSelectors.title(location, this.$i18n.locale)
                        location.pathIds = locationSelectors.pathSchema(location, this.$i18n.locale) || await this.$locationDataStore.resolvePath(location.pathIds.value, this.$i18n.locale);
                    }
                )).then(
                    () => { this.displayPaths = true }
                )
                this.pending = false;
                this.changePagePending = false;
                this.lastPageIndex = Math.floor(searchResponse.totalSize / this.pageSize);

                // To avoid extra empty page when totalSize is a multiple of pageSize
                if (searchResponse.totalSize % this.pageSize !== 0) {
                    this.lastPageIndex++;
                }

                if (resetPageIndex) {
                    this.currentPageIndex = 1;
                }
            },
            'locations-subscription')
    }

    private incrementPageIndex() {
        const newPageIndex = this.currentPageIndex + 1;
        this.currentPageIndex = newPageIndex <= this.lastPageIndex ? newPageIndex : this.lastPageIndex;
    }

    private decrementPageIndex() {
        const newPageIndex = this.currentPageIndex - 1;
        this.currentPageIndex = newPageIndex > 1 ? newPageIndex : 1;
    }

    private goToLastPage() {
        this.currentPageIndex = this.lastPageIndex;
    }

    private onItemSelected(location, index) {
        this.selectedItemIndex = index;
        this.$emit('location-changed', { location })
    }


    private toggleBarcodeReader() {
        this.isBarcodeReaderActive = !this.isBarcodeReaderActive
    }

    private onBarcodeReaderInput(value: string) {
        this.searchText = value;
        this.isBarcodeReaderActive = false;
    }

    @Watch('searchText')
    private onSearchTextChange() {
        this.value$.next(this.searchText)
    }

    @Watch('currentPageIndex')
    private onCurrentPageIndexChange() {
        this.changePagePending = true;
        this.subscribeToLocations()
        this.selectedItemIndex = -1;
        this.$emit('location-changed', { location: null })
    }

}